// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { LinkifiedText } from '@/components/linkified-text';
import {
  DeleteRounded,
  EditRounded,
  HistoryRounded,
  MoreVert,
} from '@/constants/hardfin-standard-icons';
import { Note } from '@/note/api/types';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';

interface Props {
  /**
   * The note to display
   */
  note: Note;

  /**
   * Callback when the edit button is clicked
   */
  onEdit: () => void;

  /**
   * Callback when the delete button is clicked
   */
  onDelete: () => void;

  /**
   * Callback when the audit log button is clicked
   */
  onViewAudit: () => void;

  /**
   * If true, the card is disabled
   */
  disabled?: boolean;
}

/**
 * A card that displays a note with options to edit, delete, or view its audit log.
 * The design follows a modern chat-like interface with clear attribution
 * and easy access to actions.
 */
export function NoteCard(props: Props) {
  const { note, onEdit, onDelete, onViewAudit, disabled } = props;
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  // Get initials for the avatar
  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  // Format the tooltip title with full timestamp details
  const tooltipTitle = (
    <>
      <Typography variant="body2">
        Last updated by {note.lastUpdatedBy.firstName}{' '}
        {note.lastUpdatedBy.lastName} at{' '}
        {note.lastUpdatedAt.toFormat('MMMM d, yyyy @ hh:mm:ss a (z)')}
      </Typography>
      <Typography variant="body2">
        Created by {note.createdBy.firstName} {note.createdBy.lastName} at{' '}
        {note.createdAt.toFormat('MMMM d, yyyy @ hh:mm:ss a (z)')}
      </Typography>
    </>
  );

  // Handle menu open
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  // Handle edit action
  const handleEdit = () => {
    handleMenuClose();
    onEdit();
  };

  // Handle delete action
  const handleDelete = () => {
    handleMenuClose();
    onDelete();
  };

  // Handle view audit log action
  const handleViewAudit = () => {
    handleMenuClose();
    onViewAudit();
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent sx={{ pb: 2 }}>
        {/* Header with user info and actions */}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
            {getInitials(
              note.lastUpdatedBy.firstName,
              note.lastUpdatedBy.lastName
            )}
          </Avatar>

          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1" fontWeight="medium">
              {note.lastUpdatedBy.firstName} {note.lastUpdatedBy.lastName}
            </Typography>
            <Tooltip
              arrow
              placement="bottom"
              title={tooltipTitle}
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 'none',
                    width: '40rem',
                  },
                },
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {note.lastUpdatedAt.toFormat('MMM d, yyyy • h:mm a')}
              </Typography>
            </Tooltip>
          </Box>

          <IconButton
            size="small"
            onClick={handleMenuOpen}
            disabled={disabled}
            aria-label="Note actions"
            aria-controls={isMenuOpen ? 'note-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={isMenuOpen ? 'true' : undefined}
          >
            <MoreVert fontSize="small" />
          </IconButton>

          <Menu
            id="note-menu"
            anchorEl={menuAnchorEl}
            open={isMenuOpen}
            onClose={handleMenuClose}
            MenuListProps={{
              'aria-labelledby': 'note-actions-button',
            }}
          >
            <MenuItem onClick={handleEdit} disabled={disabled}>
              <ListItemIcon>
                <EditRounded fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleViewAudit} disabled={disabled}>
              <ListItemIcon>
                <HistoryRounded fontSize="small" />
              </ListItemIcon>
              <ListItemText>View audit log</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleDelete} disabled={disabled}>
              <ListItemIcon>
                <DeleteRounded fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ color: 'error' }}>
                Delete
              </ListItemText>
            </MenuItem>
          </Menu>
        </Stack>

        {/* Note content */}
        <Typography
          variant="body1"
          sx={{
            whiteSpace: 'pre-wrap',
            paddingLeft: 1,
            borderLeft: '4px solid',
            borderColor: 'divider',
            paddingTop: 0.5,
            paddingBottom: 0.5,
          }}
        >
          <LinkifiedText>{note.content}</LinkifiedText>
        </Typography>
      </CardContent>
    </Card>
  );
}
